import React, { useState } from 'react';
import './GBOFs.scss';

// DCK Length Tiers Data
const DCK_TIERS = [
    { level: 0, name: "Tiny Little Micro PP", icon: "🦟", description: "Barely visible. Might be a speck of dust. Slaps like a butterfly kiss from a sleepy kitten." },
    { level: 1, name: "Piers' Pocket Picker", icon: "🧵", description: "Thin, flimsy, and mostly decorative. You can slap someone's pocket lint if you try really hard." },
    { level: 2, name: "Lil' Slapper Jr.", icon: "🍼", description: "Still learning motor skills. Slaps come with a complimentary baby sneeze." },
    { level: 3, name: "The Thumb Thumper", icon: "👍", description: "Basically just a thumb that grew confidence. Surprisingly decent at close-range slaps." },
    { level: 4, name: "Respectable Wiggle Stick", icon: "🐛", description: "It wiggles, it wobbles, it occasionally slaps. People are starting to notice… a little." },
    { level: 5, name: "Midrange Meat Wand", icon: "✨🥖✨", description: "Magically mediocre. Slaps do 20% more damage if you shout \"ALAKASLAP!\"" },
    { level: 6, name: "Certified Slap Rod", icon: "🚏", description: "Firm, reliable, OSHA-approved for public slappings. Carries a laminated license." },
    { level: 7, name: "The Thunder Plunger", icon: "🚽⚡️", description: "Creates suction waves with each strike. Known to unclog arguments AND toilets." },
    { level: 8, name: "Full-Size Face-Smacker", icon: "👋😮", description: "Your slaps now carry the weight of your ancestors. Bystanders gasp before you even swing." },
    { level: 9, name: "The Mega Mauler", icon: "🦍🔥", description: "This thing doesn't slap — it mauls. Users require a safety briefing before engaging." },
    { level: 10, name: "The Absolute Anaconda", icon: "🐍💥", description: "This level is classified by the USDA as \"Agricultural Equipment.\" Slaps flatten small shrubs." },
    { level: 11, name: "Galactic Girthmaster", icon: "🌌💪", description: "Slaps ripple through spacetime. Parallel universes flinch in sync." },
    { level: 12, name: "Supreme Overlord of Slap", icon: "👑👋⚡️", description: "A cosmic entity. Every slap is a divine event. Opponents bow before being struck out of respect." }
];

// Calculate total GBOFs needed to reach a level (1, 3, 6, 10, 15, 21, etc)
const getTotalGBOFsForLevel = (level) => {
    if (level === 0) return 0;
    return (level * (level + 1)) / 2;
};

const GBOFs = () => {
    const [currentLevel, setCurrentLevel] = useState(0);
    const [totalBurned, setTotalBurned] = useState(0);
    const [availableGBOFs, setAvailableGBOFs] = useState(50);
    const [burnAnimation, setBurnAnimation] = useState(false);
    const [showSimTooltip, setShowSimTooltip] = useState(false);

    const handleBurnGBOFs = () => {
        if (currentLevel >= 12) return;

        const nextLevel = currentLevel + 1;
        const costToNextLevel = getTotalGBOFsForLevel(nextLevel) - totalBurned;

        if (availableGBOFs >= costToNextLevel) {
            setBurnAnimation(true);
            setTimeout(() => setBurnAnimation(false), 600);

            setAvailableGBOFs(prev => prev - costToNextLevel);
            setTotalBurned(prev => prev + costToNextLevel);
            setCurrentLevel(nextLevel);
        }
    };

    const getCostToNextLevel = () => {
        if (currentLevel >= 12) return 0;
        return getTotalGBOFsForLevel(currentLevel + 1) - totalBurned;
    };

    const currentTier = DCK_TIERS[currentLevel];
    const costToNext = getCostToNextLevel();
    const canLevelUp = availableGBOFs >= costToNext && currentLevel < 12;

    return (
        <div className="gbofs-container">
            <div className="dck-length-system">
                <div className="dck-header">
                    <h1 className="dck-title">🔥 DCK LENGTH METER 🔥</h1>
                    <p className="dck-subtitle">Burn GBOFs to Level Up Your Slapping Power</p>
                </div>

                <div className="dck-status-card">
                    <div
                        className="simulation-mode-badge"
                        onClick={() => setShowSimTooltip(!showSimTooltip)}
                    >
                        <span className="badge-icon">🧪</span>
                        <span className="badge-text">SIMULATION MODE</span>
                        <span className="info-icon">ℹ️</span>
                        <div className={`sim-tooltip ${showSimTooltip ? 'show' : ''}`}>
                            This is a demo playground! No real GBOFs are burned until the full system is developed and released. Feel free to experiment!
                        </div>
                    </div>

                    <div className="status-header">
                        <div className="current-tier-icon">{currentTier.icon}</div>
                        <div className="status-info">
                            <h2 className="current-level">Level {currentLevel} — {currentTier.name}</h2>
                            <p className="tier-description">{currentTier.description}</p>
                        </div>
                    </div>

                    <div className="gbof-info">
                        <div className="gbof-stat">
                            <span className="stat-label">Available GBOFs:</span>
                            <span className="stat-value">{availableGBOFs}</span>
                        </div>
                        <div className="gbof-stat">
                            <span className="stat-label">Total Burned:</span>
                            <span className="stat-value burned">{totalBurned} 🔥</span>
                        </div>
                    </div>

                    {currentLevel < 12 && (
                        <div className="level-up-section">
                            <div className="progress-bar-container">
                                <div className="progress-label">
                                    <span>Progress to {DCK_TIERS[currentLevel + 1].name}</span>
                                    <span className="cost-label">Cost: {costToNext} GBOFs</span>
                                </div>
                                <div className="progress-bar">
                                    <div
                                        className="progress-fill"
                                        style={{ width: `${Math.min((availableGBOFs / costToNext) * 100, 100)}%` }}
                                    />
                                </div>
                            </div>

                            <button
                                className={`burn-button ${burnAnimation ? 'burning' : ''} ${canLevelUp ? 'can-burn' : 'cannot-burn'}`}
                                onClick={handleBurnGBOFs}
                                disabled={!canLevelUp}
                            >
                                {canLevelUp ? `🔥 BURN ${costToNext} GBOFs TO LEVEL UP 🔥` : `Need ${costToNext - availableGBOFs} More GBOFs`}
                            </button>
                        </div>
                    )}

                    {currentLevel === 12 && (
                        <div className="max-level-banner">
                            <span className="max-icon">👑</span>
                            <span className="max-text">MAXIMUM POWER ACHIEVED!</span>
                            <span className="max-icon">👑</span>
                        </div>
                    )}
                </div>

                <div className="tiers-section">
                    <h3 className="tiers-title">📏 ALL DCK LENGTH TIERS 📏</h3>
                    <div className="tiers-grid">
                        {DCK_TIERS.map((tier) => (
                            <div
                                key={tier.level}
                                className={`tier-card ${tier.level === currentLevel ? 'active' : ''} ${tier.level < currentLevel ? 'completed' : ''} ${tier.level > currentLevel ? 'locked' : ''}`}
                            >
                                <div className="tier-badge">
                                    {tier.level < currentLevel && <span className="check-mark">✓</span>}
                                    {tier.level === currentLevel && <span className="current-mark">★</span>}
                                    {tier.level > currentLevel && <span className="lock-mark">🔒</span>}
                                </div>
                                <div className="tier-icon">{tier.icon}</div>
                                <div className="tier-level">Level {tier.level}</div>
                                <div className="tier-name">{tier.name}</div>
                                <div className="tier-cost">
                                    {tier.level === 0 ? 'Starting Level' : `Total: ${getTotalGBOFsForLevel(tier.level)} GBOFs`}
                                </div>
                                <div className="tier-desc">{tier.description}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="dck-info-footer">
                    <p>💡 <strong>Pro Tip:</strong> Higher DCK Lengths unlock better rewards from the Candy Machine (Coming Soon™)</p>
                    <p>🍬 Different levels will give you access to unique candies that can combine for powerful DCKSlaps!</p>
                </div>
            </div>
        </div>
    );
};

export default GBOFs;